import { mapValues } from 'lodash/fp';
import { Component, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Positioner, PositioningStrategy } from '@optimus-web/helpers';
import { TooltipProps } from '../Tooltip';
import { noop } from '../utils/func';
import { CalendarService } from '../utils/services';
import { CalendarDayElement, DayButtonElement, DayButtonElementWithTooltip } from './style';
import { Modifiers } from './types';

export interface CalendarDayProps<D, M, Y> {
    day: D;
    endDate?: D | null;
    focusedDate: D;
    getCalendarService: () => CalendarService<D, M, Y>;
    numberOfMonths: number;
    allowDisabledClick?: boolean;
    enableAdjacentMonthSelection?: boolean;
    areSurroundingDaysOfSingleMonthHidden?: boolean;
    handleKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
    isSingleDaySelectionCircle?: boolean;
    maxDate?: D;
    minDate?: D;
    modifiers?: Modifiers<D>;
    monthSelectorVisible?: boolean;
    monthName: string;
    onDateClick?: (event: React.MouseEvent, day: D) => void;
    onDateMouseOver?: (day: D) => void;
    startDate?: D;
    startDateWithTooltip?: TooltipProps;
    dayWithTooltipRef?: React.Ref<Positioner>;
    getDateTooltip?: (day: D) => ReactNode | undefined;
    tooltipDelay?: number;
    isMobile?: boolean;
}

export default class CalendarDay<D, M, Y> extends Component<CalendarDayProps<D, M, Y>> {
    static defaultProps = {
        onDateClick: noop,
        allowDisabledClick: false,
    };

    handleClick = (e: MouseEvent) => {
        const { onDateClick, day } = this.props;

        e.preventDefault();

        if (onDateClick && day) {
            onDateClick(e, day);
        }
    };

    handleMouseOver = (e: MouseEvent) => {
        const { onDateMouseOver, day } = this.props;

        e.preventDefault();

        if (onDateMouseOver && day) {
            onDateMouseOver(day);
        }
    };

    handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
        if (this.props.handleKeyDown) {
            this.props.handleKeyDown(e);
        }
    };

    render() {
        const {
            allowDisabledClick,
            enableAdjacentMonthSelection,
            day,
            endDate,
            getCalendarService,
            modifiers,
            onDateClick,
            onDateMouseOver,
            numberOfMonths,
            focusedDate,
            monthName,
            monthSelectorVisible,
            startDate,
            startDateWithTooltip,
            dayWithTooltipRef,
            getDateTooltip,
            tooltipDelay,
            isMobile = false,
            ...other
        } = this.props;

        let dayButton;
        let appliedModifiers;

        if (this.props.day) {
            const { getDateNumber, getDateString, isDateEqual, getMonthOfDate, getMonthName } = getCalendarService();
            appliedModifiers = modifiers ? mapValues((modifier) => modifier && modifier(day), modifiers) : {};
            const dateNumber = getDateNumber(day);
            const dateString = getDateString(day);

            const currentMonthYearName = getMonthName(getMonthOfDate(day));

            const hasFocus = !monthSelectorVisible && isDateEqual(focusedDate, day);

            const isDisabledDayClickable = appliedModifiers.disabled === true && allowDisabledClick === true;
            appliedModifiers.disabled = isDisabledDayClickable ? false : appliedModifiers.disabled;
            appliedModifiers.enableAdjacentMonthSelection = enableAdjacentMonthSelection;

            const dayWithDisabledStyle = isDisabledDayClickable === true || appliedModifiers.disabled === true;

            const buttonProps = {
                ...other,
                ...appliedModifiers,
                numberOfMonths,
                onClick: this.handleClick,
                onMouseOver: this.handleMouseOver,
                onKeyDown: this.handleKeyDown,
                'data-test-date': dayWithDisabledStyle ? `disabled-${dateString}` : dateString,
                'data-date': dateString,
                focusedDate,
                monthSelectorVisible,
                isDisabledDayClickable,
                'aria-label': `${dateNumber} ${currentMonthYearName}`,
            };

            const showDayWithTooltip =
                startDateWithTooltip &&
                startDate &&
                getDateNumber(startDate) === dateNumber &&
                modifiers?.isOutOfMonth &&
                !modifiers?.isOutOfMonth(startDate) &&
                !endDate;

            const tooltip = getDateTooltip?.(day);

            const startDateTooltip =
                showDayWithTooltip && startDateWithTooltip ? { ...startDateWithTooltip, openOnMount: true } : undefined;

            const dateWithTooltip = tooltip != null ? { tooltip, tooltipHideOnClick: false } : startDateTooltip;

            if (dateWithTooltip) {
                dayButton = (numberOfMonths === 1 || !appliedModifiers.isOutOfMonth) && (
                    <DayButtonElementWithTooltip
                        tooltipDelay={tooltipDelay}
                        tabIndex={hasFocus ? 0 : -1}
                        tooltipRef={dayWithTooltipRef}
                        inline
                        inPortal={false}
                        positioningStrategy={PositioningStrategy.Absolute}
                        variant="ghost"
                        {...buttonProps}
                        {...dateWithTooltip}
                    >
                        {dateNumber}
                    </DayButtonElementWithTooltip>
                );
            } else {
                dayButton = (numberOfMonths === 1 || !appliedModifiers.isOutOfMonth) && (
                    <DayButtonElement {...buttonProps} variant="ghost" tabIndex={hasFocus ? 0 : -1}>
                        {dateNumber}
                    </DayButtonElement>
                );
            }
        }

        return (
            <CalendarDayElement isMobile={isMobile} numberOfMonths={numberOfMonths} {...appliedModifiers}>
                {dayButton}
            </CalendarDayElement>
        );
    }
}
